import type { ComponentType } from 'react';
import { flexRender } from '@tanstack/react-table';
import { useTableContext } from './TableContext';
import { joinClasses, tableStyles } from './styles';

interface SortIndicatorProps {
  isSorted: false | 'asc' | 'desc';
}

interface Props {
  sticky?: boolean;
  className?: string;
  headerCellClassNames?: Record<string, string | undefined>;
  sortable?: boolean;
  sortableClassName?: string;
  SortIndicator?: ComponentType<SortIndicatorProps>;
}

export default function TableHead({
  sticky = false,
  className,
  headerCellClassNames,
  sortable = true,
  sortableClassName,
  SortIndicator,
}: Props) {
  const { table } = useTableContext<unknown>();
  const { getHeaderGroups } = table;
  const { header, headerCell, sortableHeader } = tableStyles;

  const resolvedHeaderClassName = joinClasses(
    header,
    sticky ? 'sticky top-0 z-10' : undefined,
    className
  );

  return (
    <thead className={resolvedHeaderClassName}>
      {getHeaderGroups().map((headerGroup) => {
        const { id: headerGroupId, headers } = headerGroup;

        return (
          <tr key={headerGroupId}>
            {headers.map((headerItem) => {
              const { id, isPlaceholder, column, getContext } = headerItem;
              const {
                columnDef,
                getCanSort,
                getToggleSortingHandler,
                getIsSorted,
              } = column;
              const canSort = sortable && getCanSort();
              const customHeaderClass = headerCellClassNames
                ? headerCellClassNames[id]
                : undefined;

            return (
              <th
                key={id}
                scope="col"
                className={joinClasses(
                  headerCell,
                  customHeaderClass,
                  canSort ? (sortableClassName ?? sortableHeader) : undefined
                )}
                onClick={canSort ? getToggleSortingHandler() : undefined}
              >
                <div className="flex items-center">
                  {isPlaceholder
                    ? null
                    : flexRender(columnDef.header, getContext())}
                  {canSort && SortIndicator ? (
                    <SortIndicator
                      isSorted={getIsSorted()}
                    />
                  ) : null}
                </div>
              </th>
            );
          })}
          </tr>
        );
      })}
    </thead>
  );
}
